import {
  CheckCircleFilled,
  CloseOutlined,
  CopyOutlined,
  DeleteOutlined,
  ExclamationCircleFilled,
  ExportOutlined,
  FileSearchOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { Button, Empty, Input, message, Modal, Table, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import React, { useEffect, useState } from "react";
import {
  addSitemapPages,
  getSitemapPages,
  removeSitemapPage,
} from "../../apis/sitemap_pages.api";
import type { ISitemapPagesAddResponse } from "../../interfaces/sitemap_pages.interface";
import styles from "./sitemap_pages_tab.module.scss";

const SITE_PREFIX = "https://materialdepot.com/";

type DraftStatus = "ready" | "exists" | "invalid";

const toPath = (url: string) =>
  url.startsWith(SITE_PREFIX) ? url.slice(SITE_PREFIX.length - 1) || "/" : url;

const normalizeUrl = (raw: string) => {
  try {
    const u = new URL(raw);
    const host = u.hostname.toLowerCase();
    if (!["http:", "https:"].includes(u.protocol) || !["materialdepot.com", "www.materialdepot.com"].includes(host)) {
      return raw;
    }
    const path = u.pathname.replace(/\/+$/, "") || "/";
    return `https://materialdepot.com${path}${u.search}`;
  } catch {
    return raw;
  }
};

const parseDraft = (input: string, existing: string[]) => {
  const unique = Array.from(
    new Set(input.split(/[\s,]+/).map((u) => u.trim()).filter(Boolean).map(normalizeUrl))
  );
  return unique.map((url) => {
    let status: DraftStatus = "ready";
    if (!url.startsWith(SITE_PREFIX)) status = "invalid";
    else if (existing.includes(url)) status = "exists";
    return { url, status };
  });
};

const CHIP_CLASS: Record<DraftStatus, string> = {
  ready: styles.chipReady,
  exists: styles.chipExists,
  invalid: styles.chipInvalid,
};

const CHIP_NOTE: Record<DraftStatus, string | null> = {
  ready: null,
  exists: "already added",
  invalid: "not materialdepot.com",
};

const SitemapPagesTab: React.FC = () => {
  const [urls, setUrls] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [input, setInput] = useState("");
  const [adding, setAdding] = useState(false);
  const [result, setResult] = useState<ISitemapPagesAddResponse | null>(null);
  const [recent, setRecent] = useState<string[]>([]);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    const data = await getSitemapPages();
    if (data) setUrls(data.urls);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const draft = parseDraft(input, urls);
  const ready = draft.filter((d) => d.status === "ready").map((d) => d.url);
  const skipped = draft.length - ready.length;

  const handleAdd = async () => {
    if (!ready.length || adding) return;
    setAdding(true);
    setResult(null);
    const data = await addSitemapPages(ready);
    setAdding(false);
    if (!data) return;
    setUrls(data.urls);
    setResult(data);
    setRecent(data.added);
    setInput(data.rejected.map((r) => r.url).join("\n"));
    if (data.added.length) {
      message.success(`${data.added.length} page${data.added.length > 1 ? "s" : ""} added to sitemap`);
    }
  };

  const confirmRemove = (url: string) => {
    Modal.confirm({
      title: "Remove this page from the sitemap?",
      icon: <ExclamationCircleFilled />,
      content: (
        <>
          Google will stop seeing it in the sitemap on its next crawl.
          <div className={styles.modalUrl}>{url}</div>
        </>
      ),
      okText: "Remove",
      okButtonProps: { danger: true },
      onOk: async () => {
        const data = await removeSitemapPage(url);
        if (!data) return;
        setUrls(data.urls);
        setRecent((prev) => prev.filter((u) => u !== url));
        message.success("Page removed from sitemap");
      },
    });
  };

  const copyUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      message.success("URL copied");
    } catch {
      message.error("Could not copy URL");
    }
  };

  const query = search.trim().toLowerCase();
  const rows = urls
    .filter((url) => !query || url.toLowerCase().includes(query))
    .map((url) => ({ url }));

  const columns: ColumnsType<{ url: string }> = [
    {
      title: "Page",
      dataIndex: "url",
      render: (url: string) => (
        <div className={styles.urlCell}>
          <div className={styles.urlPath}>
            {toPath(url)}
            {recent.includes(url) && <span className={styles.newTag}>New</span>}
          </div>
          <div className={styles.urlFull}>{url}</div>
        </div>
      ),
    },
    {
      title: "",
      width: 130,
      render: (_, row) => (
        <div className={styles.actions}>
          <Tooltip title="Open page">
            <Button type="text" icon={<ExportOutlined />} href={row.url} target="_blank" rel="noreferrer" />
          </Tooltip>
          <Tooltip title="Copy URL">
            <Button type="text" icon={<CopyOutlined />} onClick={() => copyUrl(row.url)} />
          </Tooltip>
          <Tooltip title="Remove from sitemap">
            <Button danger type="text" icon={<DeleteOutlined />} onClick={() => confirmRemove(row.url)} />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div className={styles.container}>
      <div className={`${styles.card} ${styles.header}`}>
        <div className={styles.headerIcon}>
          <FileSearchOutlined />
        </div>
        <div className={styles.headerText}>
          <h3 className={styles.title}>Sitemap Pages</h3>
          <p className={styles.subtitle}>
            Pages listed here are published to Google's sitemap. Each URL is checked live before it is added.
          </p>
        </div>
        <div>
          <div className={styles.countValue}>{urls.length}</div>
          <div className={styles.countLabel}>Pages</div>
        </div>
      </div>

      <div className={styles.card}>
        <p className={styles.sectionTitle}>Add pages</p>
        <Input.TextArea
          autoSize={{ minRows: 3, maxRows: 10 }}
          value={input}
          disabled={adding}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              handleAdd();
            }
          }}
          placeholder={"Paste one URL per line, e.g.\nhttps://materialdepot.com/store/hyderabad/kompally-material-store"}
        />

        {draft.length > 0 && (
          <div className={styles.chips}>
            {draft.map((d) => (
              <span key={d.url} className={`${styles.chip} ${CHIP_CLASS[d.status]}`} title={d.url}>
                <span className={styles.chipPath}>{toPath(d.url)}</span>
                {CHIP_NOTE[d.status] && <span className={styles.chipNote}>{CHIP_NOTE[d.status]}</span>}
              </span>
            ))}
          </div>
        )}

        <div className={styles.addFooter}>
          <span className={styles.hint}>
            {adding ? (
              `Checking ${ready.length} page${ready.length > 1 ? "s" : ""}…`
            ) : draft.length ? (
              <>
                {ready.length} ready to add{skipped > 0 && ` · ${skipped} skipped`}
              </>
            ) : (
              <>
                Press <span className={styles.kbd}>Ctrl</span> + <span className={styles.kbd}>Enter</span> to add
              </>
            )}
          </span>
          {input && !adding && (
            <Button
              onClick={() => {
                setInput("");
                setResult(null);
              }}
            >
              Clear
            </Button>
          )}
          <Button
            type="primary"
            icon={<PlusOutlined />}
            loading={adding}
            disabled={!ready.length}
            onClick={handleAdd}
          >
            {ready.length > 1 ? `Add ${ready.length} pages` : "Add to sitemap"}
          </Button>
        </div>
      </div>

      {result && (result.added.length > 0 || result.rejected.length > 0) && (
        <div className={styles.result}>
          <div className={styles.resultHead}>
            <span>
              {result.added.length} added
              {result.rejected.length > 0 && ` · ${result.rejected.length} not added`}
            </span>
            <Button type="text" size="small" icon={<CloseOutlined />} onClick={() => setResult(null)} />
          </div>
          {result.added.map((url) => (
            <div key={url} className={styles.resultRow}>
              <CheckCircleFilled className={styles.resultIconOk} />
              <span className={styles.resultUrl}>{url}</span>
            </div>
          ))}
          {result.rejected.map((r) => (
            <div key={r.url} className={styles.resultRow}>
              <ExclamationCircleFilled className={styles.resultIconFail} />
              <div>
                <div className={styles.resultUrl}>{r.url}</div>
                <div className={styles.resultReason}>{r.reason}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className={styles.card}>
        <div className={styles.listHead}>
          <p className={styles.sectionTitle}>Pages in sitemap</p>
          {urls.length > 0 && (
            <Input
              allowClear
              className={styles.search}
              prefix={<SearchOutlined />}
              placeholder="Search pages"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          )}
        </div>
        <Table
          rowKey="url"
          size="middle"
          showHeader={false}
          loading={loading}
          columns={columns}
          dataSource={rows}
          rowClassName={(row) => (recent.includes(row.url) ? styles.newRow : "")}
          pagination={rows.length > 20 ? { pageSize: 20, showSizeChanger: false } : false}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={query ? "No pages match your search" : "No pages in the sitemap yet. Add one above."}
              />
            ),
          }}
        />
      </div>
    </div>
  );
};

export default SitemapPagesTab;
